import { CookiesEnum, cookiesValues } from "@/constants/constants";
import {
  getClientSideCookieValue,
  getServerSideCookieValue,
  setClientSideCookieValue,
  setServerSideCookieValue,
  isServer,
  isTvEnv,
} from "./common.helpers";
import { TypeofEnum } from "@/constants/enums";

const DEVICE_ID_COOKIE_KEY = cookiesValues[CookiesEnum.DeviceId];

type CryptoLike = {
  randomUUID?: () => string;
  getRandomValues?: (array: Uint8Array) => Uint8Array | void;
};

function isCryptoLike(value: unknown): value is CryptoLike {
  if (typeof value !== TypeofEnum.OBJECT || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.randomUUID === TypeofEnum.FUNCTION ||
    typeof v.getRandomValues === TypeofEnum.FUNCTION
  );
}

function getTVDeviceId() {
  const deviceId = window.deviceId;
  return deviceId;
}

function generateDeviceId(): string {
  try {
    // Prefer standards-based API available in browsers and modern Node runtime
    if (typeof globalThis !== TypeofEnum.UNDEFINED) {
      const cryptoLike = (globalThis as unknown as { crypto?: unknown }).crypto;
      if (
        isCryptoLike(cryptoLike) &&
        typeof cryptoLike.randomUUID === TypeofEnum.FUNCTION
      ) {
        return cryptoLike.randomUUID!() || "";
      }
      if (
        isCryptoLike(cryptoLike) &&
        typeof cryptoLike.getRandomValues === TypeofEnum.FUNCTION
      ) {
        const arr = new Uint8Array(16);
        cryptoLike.getRandomValues!(arr) || new Uint8Array(16);
        arr[6] = (arr[6] & 0x0f) | 0x40; // Version 4
        arr[8] = (arr[8] & 0x3f) | 0x80; // Variant 10xx
        return Array.from(arr)
          .map((b) => ("0" + b.toString(16)).slice(-2))
          .join("");
      }
    }
  } catch {}
  // Fallback: timestamp + random hex
  return (
    Date.now().toString(16) + Math.floor(Math.random() * 1e16).toString(16)
  );
}

function getDeviceIdClient(): string | undefined {
  return getClientSideCookieValue(DEVICE_ID_COOKIE_KEY);
}

function setDeviceIdClient(id: string) {
  if (typeof document === TypeofEnum.UNDEFINED) return;

  const isSecure =
    typeof window !== TypeofEnum.UNDEFINED &&
    window.location.protocol === "https:";

  setClientSideCookieValue(DEVICE_ID_COOKIE_KEY, id, {
    secure: isSecure,
  });
}

export async function getOrCreateDeviceIdClient(): Promise<string> {
  const existing = getDeviceIdClient();
  if (existing) return existing;

  // Try to get device ID first if on TV
  // If it fails or returns null, fallback to generated device ID
  if (isTvEnv()) {
    try {
      const tvDeviceId = getTVDeviceId();
      if (tvDeviceId && tvDeviceId !== "") {
        setDeviceIdClient(tvDeviceId);
        return tvDeviceId;
      }
    } catch (error) {
      console.error("Error retrieving device ID, using fallback:", error);
    }
  }

  // Fallback to generated device ID
  const id = generateDeviceId();
  setDeviceIdClient(id);
  return id;
}

async function getDeviceIdServer(): Promise<string | undefined> {
  try {
    return await getServerSideCookieValue(DEVICE_ID_COOKIE_KEY);
  } catch {
    return undefined;
  }
}

async function getOrCreateDeviceIdServer(): Promise<string> {
  const existing = await getDeviceIdServer();
  if (existing) return existing;
  const id = generateDeviceId();
  try {
    await setServerSideCookieValue(DEVICE_ID_COOKIE_KEY, id);
  } catch {}
  return id;
}

export async function getDeviceId(): Promise<string | undefined> {
  if (isServer()) {
    return await getDeviceIdServer();
  }
  return getDeviceIdClient();
}

export async function getOrCreateDeviceId(): Promise<string> {
  if (isServer()) {
    return await getOrCreateDeviceIdServer();
  }
  return getOrCreateDeviceIdClient();
}
